import { Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { RecursivePartial } from '@models/apollo';
import { TranslateService } from '@ngx-translate/core';
import { Order, ShoppingCart } from '@queries/generated';
import { AblyChannel, AblyChannelsService } from '@services/ably-channels.service';
import { StripeService } from '@services/stripe.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'vnm-stripe-payment',
  templateUrl: './stripe-payment.component.html',
  styleUrls: ['./stripe-payment.component.scss']
})
export class StripePaymentComponent implements OnInit {
  @ViewChild('paymentElement', { static: true }) paymentElm: ElementRef;
  @ViewChild('submitButton', { static: true }) submitElm: ElementRef<HTMLButtonElement>;

  @Input() shoppingCart!: ShoppingCart;
  @Output() paymentSuccess = new EventEmitter<RecursivePartial<Order>>();

  stripe: any;
  elements: any;

  order: RecursivePartial<Order>;
  paymentChannel: AblyChannel;
  ablyTimeout: ReturnType<typeof setTimeout>;

  constructor(
    private stripeService: StripeService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private ablyChannels: AblyChannelsService,
    private zone: NgZone
  ) {}

  get paymentElement() {
    return this.paymentElm?.nativeElement;
  }

  get submitButton() {
    return this.submitElm?.nativeElement;
  }

  ngOnInit() {
    this.buildForm();
  }

  async onSubmit() {
    // Prevent multiple form submissions
    if (this.submitButton.disabled) {
      return;
    }

    // Disable form submission while loading
    this.submitButton.disabled = true;

    // Trigger form validation and wallet collection
    const { error: submitError } = await this.elements.submit();

    if (submitError) {
      this.handleError(submitError);
      return;
    }

    const { error: paymentError, paymentMethod } = await this.stripe.createPaymentMethod({
      elements: this.elements
    });

    if (paymentError) {
      // This point is only reached if there's an immediate error when
      // creating the PaymentMethod. Show the error to your customer (for example, payment details incomplete)
      this.handleError(paymentError);
      return;
    }

    // Create the PaymentIntent
    this.stripeService.createOrder({ gatewayInfo: { token: paymentMethod.id, intent: true } }).subscribe(
      ({ gatewayInfo, order }) => this.handleServerResponse(gatewayInfo, order),
      (error) => this.handleError(error)
    );
  }

  buildForm() {
    const { shoppingCart } = this;
    this.stripe = Stripe(shoppingCart.storefront.stripePublishableKey);

    const options = {
      mode: 'payment',
      amount: this.shoppingCart.expectedTotalCents,
      currency: shoppingCart.storefront.currency.isoCode.toLowerCase(),
      paymentMethodCreation: 'manual',
      appearance: {
        theme: 'stripe'
      }
    };
    this.elements = this.stripe.elements(options);

    const {
      country: { code: country },
      postalCode: postal_code
    } = shoppingCart.billingAddress;

    // Set up Stripe.js and Elements to use in checkout form
    const paymentElement = this.elements.create('payment', {
      defaultValues: { billingDetails: { address: { country, postal_code } } },
      wallets: { applePay: 'never', googlePay: 'never' }
    });
    paymentElement.mount(this.paymentElement);
  }

  handleServerResponse = async (gatewayInfo, order) => {
    this.order = order;
    this.zone.run(() => {
      this.paymentChannel = this.ablyChannels.get(order.channelData);
      this.paymentChannel.subscribe('push', (data) => {
        if (data?.data?.content?.message == 'completed') {
          this.handleSuccess(order);
        }
      });
    });

    if (gatewayInfo.status === 'requires_action') {
      // Use Stripe.js to handle the required next action
      const { error } = await this.stripe.handleNextAction({
        clientSecret: gatewayInfo.clientSecret
      });

      if (error) {
        this.handleError(error);
      }
    } else if (gatewayInfo.status === 'succeeded') {
      this.ablyTimeout = setTimeout(() => this.handleSuccess(order), 3000);
    }
  };

  handleError({ message: summary }) {
    this.messageService.add({
      severity: 'error',
      summary
    });

    this.submitButton.disabled = false;
  }

  handleSuccess(order: RecursivePartial<Order>) {
    clearTimeout(this.ablyTimeout);

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('checkout.success')
    });

    this.paymentSuccess.emit(order);
  }
}
